/**
 * Performance component module.
 * @module shared/components/Performance
 */
import React from 'react';
import PropTypes from 'prop-types';
import {_openModal} from '../reducers/modals';
import {connect} from 'react-redux';
import useSWR from 'swr';
import {Column, Row, DescriptiveText, Dropdown, LoadingSpinner} from '../index';
import {BenchmarkSelector} from '../../advisor';
import useQueryString from '../utils/useQueryString';
import {_getPeriods, getDefaultTimePeriod} from '../reducers/periods';
import ColumnGraph from '../components/graphs/ColumnGraph';
import {SizeMe} from 'react-sizeme';
import Performance from '../components/performance/Performance';
import {formatter, parseErrorMessage, getPreferencesValue} from '../utils/utils';
import {_setNotification, isEntitled} from '../store';
import axiosInstance from '../utils/axios';
import {ColorBox} from '../utils/colors';
import useQueryObject from '../utils/useQueryObject';
import {withTheme} from 'styled-components/macro';
import lazy from 'lazy.js';
import moment from 'moment';
import {PORTAL_NAME} from '../../config.json';

const benchmarkColor = '#979797';
const dateFormatter = formatter('date');
const preferencesPath = ['investorPerformanceWeb', 'investorPerformanceWeb', 'investorDefaultPerformancePeriod'];
const isInvestorPortal = PORTAL_NAME === 'investor';

const PerformanceGraph = ({containerID, periods, getPeriods, openModal, theme, setNotification, context, session}) => {
  const [graphContext] = useQueryObject('context', context);
  const [period, setPeriod] = isInvestorPortal ? React.useState() : useQueryString('performancePeriod');
  const investorPreferencesDataUrl = `/preferences/users/${session.profile.id}/panels/investorGeneralWeb`;
  const {data: investorPreferences, isValidating: investorPreferencesLoading} = useSWR(isInvestorPortal && investorPreferencesDataUrl);
  const investorDefaultPeriod = React.useMemo(() => investorPreferences
      && getPreferencesValue(investorPreferences, ...preferencesPath), [investorPreferences]);
  // Performance
  const {data: container} = useSWR(containerID && `/containers/${containerID}`);
  const performance = React.useMemo(() => container && container.performance, [container]);

  // Periods
  React.useEffect(() => {getPeriods();}, []);
  const [timePeriod, setTimePeriod] = React.useState();

  const _periods = React.useMemo(() => performance && lazy(periods)
    .filter(p => Object.keys(performance).includes(p.id))
    .map(p => p.id === 'MAX' ? {
      ...p,
      start: container.start,
      days: moment().diff(moment(container.start, 'YYYY-MM-DD'), 'd'),
    } : p)
    .sortBy('days')
    .toArray(), [performance, periods]);

  React.useEffect(() => {
    if (isInvestorPortal && !investorDefaultPeriod) return;
    if (_periods?.length && performance) {
      const defaultPeriod = investorDefaultPeriod || period;
      const periodIDs = Object.keys(performance);
      if (periodIDs.length) {
        setTimePeriod(getDefaultTimePeriod(
          _periods.find(p => p.id === defaultPeriod),
          container.start
        ));
      }
    }
  }, [_periods, performance, investorDefaultPeriod]);

  React.useEffect(() => {
    if (timePeriod) {
      setPeriod(timePeriod.id);
    }
  }, [timePeriod]);

  // Benchmarks
  const entitledBenchmarks = isEntitled([{group: 'benchmarks', feature: 'core', access: 'r'}]);
  const [benchmark, setBenchmark] = React.useState();
  const [benchmarkPerformance, setBenchmarkPerformance] = React.useState();

  const changeBenchmark = newBenchmark => {
    setBenchmark(newBenchmark);

    if (!newBenchmark.id) {
      setBenchmarkPerformance(null);
      return;
    }

    const start = lazy(_periods).sortBy('start').get(0).start;

    return axiosInstance.get(`/benchmarks/${newBenchmark.id}/performance`, {params: {start}})
      .then(res => setBenchmarkPerformance(res.data))
      .catch(err => setNotification({message: parseErrorMessage(err)}));
  };

  // Performance Graph
  const formattedData = React.useMemo(() => {
    if (!performance || (entitledBenchmarks && (!benchmark || (benchmark.id && !benchmarkPerformance)))) {
      return null;
    }
    const data = [];

    if (performance) {
      data.push({
        name: graphContext.name,
        color: theme.primaryColor,
        data: _periods.map(p => ({
          id: p.id,
          name: p.name,
          y: performance[p.id]
            ? Number(((Math.round(((performance[p.id] || 0) * 100) * 10) / 10).toFixed(1)))
            : 0,
        })),
      });
    }
    if (benchmarkPerformance) {
      data.push({
        name: 'Benchmark',
        color: benchmarkColor,
        enableMouseTracking: false,
        data: _periods.map(p => ({
          id: p.id,
          name: p.name,
          y: benchmarkPerformance[p.id]
            ? Number(((Math.round(((benchmarkPerformance[p.id] || 0) * 100) * 10) / 10).toFixed(1)))
            : 0,
        })),
      });
    }
    return data;
  }, [performance, benchmarkPerformance, _periods]);

  // Render
  if (!performance || !_periods || investorPreferencesLoading) {
    return <LoadingSpinner/>;
  }

  if (!timePeriod) {
    return <DescriptiveText>There is not yet enough data to display performance</DescriptiveText>;
  }
  return <SizeMe monitorHeight={true}>
    {({size}) => <Column height={'100px'}
                         width={'100%'}
                         alignSubRow={'stretch'}
                         alignItems={'stretch'}
                         layout={{grow: 1}}>
      {/*Small Performance Tile*/}
      {size.width < 500
      && <Column>
        <Dropdown onChange={(name, value) => setTimePeriod(value.value)}
                  title={'Time Period'}
                  options={_periods.map(p => ({label: p.name, value: p}))}
                  dataPoint={{
                    name: 'timePeriod',
                    value: {label: timePeriod.name, value: timePeriod},
                  }}
                  placeholder={'Select a Period'}/>
        <DescriptiveText preset={'dataLabel'}>{dateFormatter(timePeriod.start)} - {dateFormatter(timePeriod.end)}</DescriptiveText>
        <Performance performance={performance[timePeriod.id]}
                     size={'lg'}
                     labelPosition={'bottom'}
                     action={() => openModal({type: 'PerformanceSecuritiesModal', period: timePeriod, containerID})}/>
        {/*Benchmark Row*/}
        {!isInvestorPortal
            && entitledBenchmarks
            && <Row wrap={false}>
              <BenchmarkSelector width={'50%'} onChange={changeBenchmark} showDisclosure={true}/>
              <Row width={'50%'}>
                {benchmark?.id
            && (benchmarkPerformance
              ? <Performance performance={benchmarkPerformance[timePeriod.id]} size={'sm'} labelPosition={'right'}/>
              : <LoadingSpinner/>) || <span>&nbsp;</span>
                }
              </Row>
            </Row>}
      </Column>}
      {/*Large Performance Tile/Tile Detail View*/}
      {size.width >= 500 && <>
        <Row justifySubRow={'left'} alignItems={'top'}>
          <Column height={'fit-content'} width={'fit-content'} alignItems={'left'} margin={'5px'}>
            <Row width={'fit-content'} verticalAlign={true}>
              <ColorBox color={theme.primaryColor}/>
              <DescriptiveText preset={'standard'}>{graphContext.name}</DescriptiveText>
            </Row>
            {benchmark?.id && <Row width={'fit-content'} verticalAlign={true}>
              <ColorBox color={benchmarkColor}/>
              <DescriptiveText preset={'standard'}>Benchmark</DescriptiveText>
            </Row>}
          </Column>
          {!isInvestorPortal
              && entitledBenchmarks
              && <BenchmarkSelector width={'250px'} showDisclosure={true} onChange={changeBenchmark}/>}
        </Row>
        {formattedData
          ? <ColumnGraph hideLegend={true} height={`${size.height - 80}px`} data={formattedData} action={p => {
            openModal({
              type: 'PerformanceSecuritiesModal',
              period: p,
              containerID,
            });
          }}/>
          : <LoadingSpinner/>}
      </>}
    </Column>}
  </SizeMe>;
};

const mapStateToProps = state => ({
  periods: state.periods,
  session: state.session,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
  getPeriods: () => dispatch(_getPeriods()),
  setNotification: notification => dispatch(_setNotification(notification)),
});
export default withTheme(connect(mapStateToProps, mapDispatchToProps)(PerformanceGraph));

PerformanceGraph.propTypes = {
  session: PropTypes.shape({
    profile: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }),
  }).isRequired,
  openModal: PropTypes.func,
  getPeriods: PropTypes.func.isRequired,
  periods: PropTypes.array.isRequired,
  containerID: PropTypes.number.isRequired,
  setNotification: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  context: PropTypes.object,
};
